import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { systemService } from "../../services/api";
import Button from "../common/Button";
import LogEvidence, { LogActor, LogSubject } from "./SystemLogEvidence";
import { buildLogParams, formatLogTimestamp } from "./systemLogPresentation";

const Container = styled.section`
  margin-top: 24px;
  min-width: 0;
  color: ${({ theme }) => theme.colors.dark};
`;
const Heading = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  h2 {
    margin: 0 0 8px;
  }
  p {
    margin: 0;
  }
`;
const Panel = styled.div`
  padding: 20px;
  margin-top: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: white;
  min-width: 0;
  @media (max-width: 600px) {
    padding: 12px;
  }
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;
const Filters = styled.form`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  min-width: 0;
`;
const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font: inherit;
  background: white;
`;
const Select = styled(Input).attrs({ as: "select" })``;
const FullWidth = styled.div`
  grid-column: 1 / -1;
`;
const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.medium};
  line-height: 1.5;
  overflow-wrap: anywhere;
`;
const Notice = styled.div`
  padding: 12px;
  border-radius: 4px;
  background: ${({ $error }) => ($error ? "#fff1f0" : "#eef6f9")};
  color: ${({ $error }) => ($error ? "#8a2424" : "#244653")};
  margin: 12px 0;
  overflow-wrap: anywhere;
`;
const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin: 16px 0;
  font-size: 14px;
`;
const Scroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th {
    text-align: left;
    background: #f4f6f7;
    color: #46535c;
    font-size: 12px;
  }
  th,
  td {
    padding: 12px;
    border-bottom: 1px solid #e1e6e9;
    vertical-align: top;
  }
  td {
    overflow-wrap: anywhere;
  }
  th:first-child,
  td:first-child {
    min-width: 150px;
  }
  @media (max-width: 600px) {
    th,
    td {
      padding: 8px;
    }
  }
`;
const Badge = styled.span`
  display: inline-block;
  padding: 3px 7px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $level }) =>
    $level === "error"
      ? "#fee3e0"
      : $level === "warning"
      ? "#fff0cf"
      : "#e7f0f4"};
  color: #354651;
`;
const LinkButton = styled.button`
  border: 0;
  background: none;
  color: #19617e;
  text-decoration: underline;
  padding: 4px 0;
  cursor: pointer;
  font: inherit;
  text-align: left;
`;
const DEFAULT_FILTERS = {
  search: "",
  type: "all",
  level: "all",
  startDate: "",
  endDate: "",
  workDate: "",
  action: "",
  requestId: "",
  actorUserId: "",
  employeeId: "",
  customerId: "",
  timeEntryId: "",
  page: 1,
  limit: 25,
  sortOrder: "DESC",
};
const LOG_TYPES = [
  ["system", "System"],
  ["auth", "Authentication"],
  ["user", "User"],
  ["api", "API"],
  ["database", "Database"],
  ["support", "Support / impersonation"],
  ["timereporting", "Timereporting audit"],
  ["integration_admin", "Privileged administration"],
  ["configuration_admin", "Configuration"],
  ["ai", "Ask AI"],
  ["invoicing", "Invoicing"],
];

export default function SystemLogs() {
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [results, setResults] = useState({
    logs: [],
    pagination: { total: 0, page: 1, limit: 25, totalPages: 0 },
    summary: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const alive = useRef(true);
  const initialLogId = useRef(
    new URLSearchParams(window.location.search).get("logId")
  );
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    systemService
      .getSystemLogs(buildLogParams(filters), { signal: controller.signal })
      .then((response) => {
        if (!active) return;
        setResults({
          logs: response.data?.logs || [],
          pagination: response.data?.pagination || {
            total: 0,
            page: 1,
            limit: 25,
            totalPages: 0,
          },
          summary: response.data?.summary || {},
        });
        setLoading(false);
      })
      .catch(() => {
        if (active) {
          setLoading(false);
          setError(
            "Unable to load logs. Check your connection and access, then retry."
          );
        }
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [filters, refresh]);
  const change = (event) =>
    setDraft((previous) => ({
      ...previous,
      [event.target.name]: event.target.value,
    }));
  const apply = (event) => {
    event.preventDefault();
    if (
      draft.startDate &&
      draft.endDate &&
      new Date(draft.startDate) > new Date(draft.endDate)
    ) {
      setError("The end of the recorded time range must be after its start.");
      return;
    }
    setNotice("");
    setExpanded(null);
    setFilters({ ...draft, page: 1 });
  };
  const focusHistory = (query) => {
    const next = { ...DEFAULT_FILTERS, ...query, sortOrder: "ASC" };
    setDraft(next);
    setFilters(next);
    setExpanded(null);
    setNotice(
      "Showing the complete recorded history for the selected request or entry. Other filters have been cleared."
    );
  };
  const exportLogs = async () => {
    setExporting(true);
    setNotice("");
    try {
      const response = await systemService.exportSystemLogs(
        buildLogParams(filters)
      );
      if (!alive.current) return;
      const data = response.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `tempus-logs-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setNotice(
        data.truncated
          ? `Export limited: downloaded ${data.returned} of ${data.total} matching events (maximum ${data.limit}). Narrow the filters to export the remaining events.`
          : `Downloaded all ${
              data.returned ?? data.logs?.length ?? 0
            } matching events using the applied search filters.`
      );
    } catch {
      if (alive.current)
        setNotice(
          "Export failed. No complete download was generated. Please retry."
        );
    } finally {
      if (alive.current) setExporting(false);
    }
  };
  const clearOperationalLogs = async () => {
    if (
      !window.confirm(
        "Clear operational logs for the selected type and level? Search text, people and date filters do not limit this cleanup. Protected audit records are retained. This cannot be undone."
      )
    )
      return;
    setClearing(true);
    try {
      await systemService.clearSystemLogs({
        type: filters.type === "all" ? undefined : filters.type,
        level: filters.level === "all" ? undefined : filters.level,
      });
      if (alive.current) {
        setRefresh((value) => value + 1);
        setNotice(
          "Operational log cleanup completed. Protected audit records were retained."
        );
      }
    } catch {
      if (alive.current)
        setNotice(
          "Unable to clear operational logs. No cleanup has been confirmed."
        );
    } finally {
      if (alive.current) setClearing(false);
    }
  };
  const { logs, pagination, summary } = results;
  const page = Number(pagination.page) || 1;
  const pages = Number(pagination.totalPages) || 1;
  const total = Number(pagination.total) || 0;
  const detailProps = {
    onRequest: (requestId) => focusHistory({ requestId }),
    onEntry: (timeEntryId) => focusHistory({ timeEntryId }),
  };
  return (
    <Container aria-label="System log investigation">
      <Heading>
        <div>
          <h2>System Logs</h2>
          <p>
            Find what happened, when it happened and which account made the
            change.
          </p>
        </div>
        <Row>
          <Button
            variant="outline"
            onClick={() => setRefresh((value) => value + 1)}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={exportLogs}
            disabled={loading || exporting || !!error || !total}
          >
            {exporting ? "Preparing export…" : "Export matching logs"}
          </Button>
        </Row>
      </Heading>
      <Panel>
        <Filters onSubmit={apply}>
          <FullWidth>
            <Label>
              Search activity
              <Input
                name="search"
                value={draft.search}
                onChange={change}
                placeholder="Name, email, customer, action, error or request ID"
              />
            </Label>
            <Muted>
              Search recorded messages and searchable details, including
              employee and customer names.
            </Muted>
          </FullWidth>
          <Label>
            Log type
            <Select name="type" value={draft.type} onChange={change}>
              <option value="all">All log types</option>
              {LOG_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Label>
          <Label>
            Severity
            <Select name="level" value={draft.level} onChange={change}>
              <option value="all">All severities</option>
              {["error", "warning", "info", "debug"].map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </Select>
          </Label>
          <Label>
            Recorded from (local time)
            <Input
              type="datetime-local"
              name="startDate"
              value={draft.startDate}
              onChange={change}
            />
          </Label>
          <Label>
            Recorded until (local time)
            <Input
              type="datetime-local"
              name="endDate"
              value={draft.endDate}
              onChange={change}
            />
          </Label>
          <div>
            <Label>
              Workday
              <Input
                type="date"
                name="workDate"
                value={draft.workDate}
                onChange={change}
              />
            </Label>
            <Muted>The day worked; different from when it was saved.</Muted>
          </div>
          <Label>
            Action
            <Input
              name="action"
              value={draft.action}
              onChange={change}
              placeholder="e.g. hours_logged"
            />
          </Label>
          <Label>
            Request ID
            <Input
              name="requestId"
              value={draft.requestId}
              onChange={change}
              placeholder="Correlate one request"
            />
          </Label>
          <Label>
            Event order
            <Select name="sortOrder" value={draft.sortOrder} onChange={change}>
              <option value="DESC">Newest first</option>
              <option value="ASC">Oldest first (timeline)</option>
            </Select>
          </Label>
          <FullWidth>
            <details>
              <summary>Filter by record IDs</summary>
              <Row style={{ marginTop: 12 }}>
                {[
                  ["actorUserId", "Acting user ID"],
                  ["employeeId", "Employee ID"],
                  ["customerId", "Customer ID"],
                  ["timeEntryId", "Time entry ID"],
                ].map(([name, label]) => (
                  <Label key={name}>
                    {label}
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      name={name}
                      value={draft[name]}
                      onChange={change}
                    />
                  </Label>
                ))}
              </Row>
            </details>
          </FullWidth>
          <FullWidth>
            <Row>
              <Button type="submit">Search logs</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDraft(DEFAULT_FILTERS);
                  setFilters({ ...DEFAULT_FILTERS });
                  setNotice("");
                  setExpanded(null);
                }}
              >
                Reset filters
              </Button>
              <Muted>
                Changes to filters apply when you select Search logs.
              </Muted>
            </Row>
          </FullWidth>
        </Filters>
      </Panel>
      {notice && <Notice role="status">{notice}</Notice>}
      {initialLogId.current && /^\d+$/.test(initialLogId.current) && (
        <Panel>
          <LogEvidence id={initialLogId.current} {...detailProps} />
        </Panel>
      )}
      <Panel aria-busy={loading}>
        <Muted>
          Times use your browser timezone. Expand an event to see its UTC
          timestamp, recorded evidence and related actions. Exports include up
          to 5,000 matching events and disclose any truncation.
        </Muted>
        <Stats aria-label="Search totals">
          <span>
            <strong>{summary.matchingEvents ?? total}</strong> matching events
          </span>
          <span>
            <strong>{summary.errors ?? 0}</strong> errors
          </span>
          <span>
            <strong>{summary.warnings ?? 0}</strong> warnings
          </span>
        </Stats>
        {error ? (
          <Notice role="alert" $error>
            {error}{" "}
            <LinkButton onClick={() => setRefresh((value) => value + 1)}>
              Retry search
            </LinkButton>
          </Notice>
        ) : loading ? (
          <Notice role="status">Loading matching events…</Notice>
        ) : !logs.length ? (
          <Notice>
            No logs match these filters. Try widening the recorded date range or
            searching by the workday. Missing logs are not proof that an action
            did not happen.
          </Notice>
        ) : (
          <>
            <Scroll>
              <Table aria-label="System log search results">
                <thead>
                  <tr>
                    <th>Recorded at</th>
                    <th>Acting account</th>
                    <th>What happened</th>
                    <th>Employee / customer</th>
                    <th>Type / severity</th>
                    <th>Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr>
                        <td>
                          {formatLogTimestamp(log.timestamp)}
                          <Muted>Log #{log.id}</Muted>
                        </td>
                        <td>
                          <LogActor log={log} />
                        </td>
                        <td>
                          {log.summary || log.message}
                          {log.changeSummary && (
                            <Muted>{log.changeSummary}</Muted>
                          )}
                          {log.outcome && <Muted>Outcome: {log.outcome}</Muted>}
                        </td>
                        <td>
                          <LogSubject log={log} />
                        </td>
                        <td>
                          <div>
                            {LOG_TYPES.find(
                              ([type]) => type === log.type
                            )?.[1] || log.type}
                          </div>
                          <Badge $level={log.level}>{log.level}</Badge>
                        </td>
                        <td>
                          <LinkButton
                            aria-expanded={expanded === log.id}
                            aria-controls={`log-evidence-${log.id}`}
                            onClick={() =>
                              setExpanded((value) =>
                                value === log.id ? null : log.id
                              )
                            }
                          >
                            {expanded === log.id ? "Hide" : "Inspect"} #{log.id}
                          </LinkButton>
                        </td>
                      </tr>
                      {expanded === log.id && (
                        <tr>
                          <td colSpan={6}>
                            <div id={`log-evidence-${log.id}`}>
                              <LogEvidence id={log.id} {...detailProps} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </Scroll>
            <Row style={{ justifyContent: "space-between", marginTop: 16 }}>
              <Muted>
                Showing {(page - 1) * pagination.limit + 1}–
                {Math.min(page * pagination.limit, total)} of {total} · Page{" "}
                {page} of {pages}
              </Muted>
              <Row>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() =>
                    setFilters((previous) => ({ ...previous, page: 1 }))
                  }
                >
                  First
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() =>
                    setFilters((previous) => ({ ...previous, page: page - 1 }))
                  }
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= pages}
                  onClick={() =>
                    setFilters((previous) => ({ ...previous, page: page + 1 }))
                  }
                >
                  Next
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= pages}
                  onClick={() =>
                    setFilters((previous) => ({ ...previous, page: pages }))
                  }
                >
                  Last
                </Button>
              </Row>
            </Row>
          </>
        )}
      </Panel>
      <details style={{ marginTop: 20 }}>
        <summary>Operational log cleanup</summary>
        <Muted>
          Cleanup uses the applied log type and severity only. It ignores search
          text, dates and record IDs. Protected audit records are retained.
        </Muted>
        <Button
          variant="outline"
          size="sm"
          disabled={clearing}
          onClick={clearOperationalLogs}
        >
          {clearing ? "Clearing…" : "Clear operational logs"}
        </Button>
      </details>
    </Container>
  );
}
